import React from 'react';
import { Divider, Paper, Stack, Typography } from '@mui/material';
import { formatInr } from './razorpay';

export const GST_MODE_OPTIONS = [
  { value: 'exclusive', label: 'Exclusive — amount is before GST' },
  { value: 'inclusive', label: 'Inclusive — amount includes GST' },
];

function round2(n) {
  return Math.round(n * 100) / 100;
}

/** Resolve taxable base, GST, and gross total from billing (snapshot or computed). */
export function resolveContractGst(billing = {}) {
  const snapshot = billing.gstSnapshot;
  if (snapshot && snapshot.grossAmount != null) {
    return {
      taxableValue: snapshot.taxableValue ?? 0,
      totalTax: snapshot.totalTax ?? 0,
      grossAmount: snapshot.grossAmount,
      gstMode: snapshot.gstMode || billing.gstMode || 'exclusive',
      gstRate: snapshot.gstRate ?? billing.gstRate ?? 18,
    };
  }

  const amount = Number(billing.amount);
  if (!Number.isFinite(amount)) return null;

  const gstMode = billing.gstMode || 'exclusive';
  const gstRate = Number(billing.gstRate ?? 18);
  const rate = gstRate / 100;

  let taxableValue;
  let totalTax;
  let grossAmount;

  if (gstMode === 'inclusive') {
    grossAmount = amount;
    taxableValue = grossAmount / (1 + rate);
    totalTax = grossAmount - taxableValue;
  } else {
    taxableValue = amount;
    totalTax = taxableValue * rate;
    grossAmount = taxableValue + totalTax;
  }

  return {
    taxableValue: round2(taxableValue),
    totalTax: round2(totalTax),
    grossAmount: round2(grossAmount),
    gstMode,
    gstRate,
  };
}

export function formatContractBillingDisplay(billing) {
  const gst = resolveContractGst(billing);
  if (!gst) return { primary: '—', secondary: null };

  const { taxableValue, totalTax, grossAmount, gstMode, gstRate } = gst;
  const primary = formatInr(grossAmount);
  const secondary =
    gstMode === 'inclusive'
      ? `${formatInr(taxableValue)} base + ${formatInr(totalTax)} GST (${gstRate}%)`
      : `${formatInr(taxableValue)} + ${formatInr(totalTax)} GST (${gstRate}%)`;

  return { primary, secondary };
}

export function ContractBillingPreview({ amount, gstMode, gstRate }) {
  const gst = resolveContractGst({
    amount: Number(amount),
    gstMode,
    gstRate: Number(gstRate),
  });

  if (!gst) {
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: 'rgba(15,23,42,0.02)',
          borderColor: 'rgba(15,23,42,0.08)',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Enter billing amount to see Amount + GST = total.
        </Typography>
      </Paper>
    );
  }

  const amountLabel = gstMode === 'inclusive' ? 'Amount (base)' : 'Amount';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'rgba(37,99,235,0.04)',
        borderColor: 'rgba(37,99,235,0.2)',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5 }}>
        Billing summary
      </Typography>
      <Stack spacing={1}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            {amountLabel}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatInr(gst.taxableValue)}
          </Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            GST ({gst.gstRate}%)
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {formatInr(gst.totalTax)}
          </Typography>
        </Stack>
        <Divider sx={{ borderColor: 'rgba(15,23,42,0.08)' }} />
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Total amount
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 800, color: '#2563eb' }}>
            {formatInr(gst.grossAmount)}
          </Typography>
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
        {formatInr(gst.taxableValue)} + {formatInr(gst.totalTax)} GST = {formatInr(gst.grossAmount)}
        {gstMode === 'inclusive' ? ' (billing amount is inclusive of GST)' : ''}
      </Typography>
    </Paper>
  );
}
